import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CartSidebar from "@/components/feature/CartSidebar";
import { trackOrder, type OrderDetail } from "@/lib/api";

const STATUS_STEPS = [
  { key: "pending", label: "Order Placed", icon: "ri-file-list-3-line" },
  { key: "confirmed", label: "Confirmed", icon: "ri-checkbox-circle-line" },
  { key: "processing", label: "Processing", icon: "ri-settings-3-line" },
  { key: "shipped", label: "Shipped", icon: "ri-truck-line" },
  { key: "delivered", label: "Delivered", icon: "ri-home-smile-line" },
];

function getStatusIndex(status: string) {
  if (status === "cancelled") return -1;
  return STATUS_STEPS.findIndex((s) => s.key === status);
}

export default function OrderTrackingPage() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) {
      setError("Please enter both order number and email.");
      return;
    }

    setLoading(true);
    setError("");
    setOrder(null);

    try {
      const data = await trackOrder(orderNumber.trim(), email.trim());
      setOrder(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const statusIdx = order ? getStatusIndex(order.status) : -1;

  return (
    <div className="min-h-screen bg-moonlight" style={{ fontFamily: "'Jost', sans-serif" }}>
      <Navbar />
      <CartSidebar />

      <div className="pt-28 pb-20 px-5 max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1
            className="text-3xl md:text-4xl font-light italic text-midnight mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Track Your Order
          </h1>
          <p className="text-sm text-midnight/50">
            Enter your order number and email to check the status of your order.
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleTrack} className="bg-white rounded-xl p-6 md:p-8 border border-pearl/40 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-midnight/40 mb-2">
                Order Number
              </label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => { setOrderNumber(e.target.value); setError(""); }}
                className="w-full border border-pearl/60 rounded-lg px-4 py-3 text-sm text-midnight outline-none focus:border-midnight transition-colors"
                placeholder="e.g. IIF-ABC123..."
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-midnight/40 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                className="w-full border border-pearl/60 rounded-lg px-4 py-3 text-sm text-midnight outline-none focus:border-midnight transition-colors"
                placeholder="The email used for your order"
              />
            </div>
          </div>

          {error && (
            <div className="mb-4 text-sm text-red-500 bg-red-50 rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-midnight text-moonlight rounded-lg py-3.5 text-xs tracking-widest uppercase hover:bg-midnight/90 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
          >
            {loading ? "Searching..." : "Track Order"}
          </button>
        </form>

        {/* Order Result */}
        {order && (
          <div className="bg-white rounded-xl border border-pearl/40 overflow-hidden">
            {/* Order header */}
            <div className="px-6 md:px-8 py-6 border-b border-pearl/30">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-medium text-midnight">{order.orderNumber}</h2>
                  <p className="text-xs text-midnight/40 mt-1">
                    Placed on {new Date(order.createdAt).toLocaleDateString("en-US", {
                      year: "numeric", month: "long", day: "numeric",
                    })}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-light text-midnight">${order.total.toFixed(2)}</div>
                  <div className="text-xs text-midnight/40">{order.items.length} item{order.items.length > 1 ? "s" : ""}</div>
                </div>
              </div>
            </div>

            {/* Status Timeline */}
            <div className="px-6 md:px-8 py-8">
              {order.status === "cancelled" ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-close-circle-line text-2xl text-red-400"></i>
                  </div>
                  <p className="text-sm font-medium text-red-500">Order Cancelled</p>
                  <p className="text-xs text-midnight/40 mt-1">This order has been cancelled.</p>
                </div>
              ) : (
                <div className="flex items-center justify-between relative">
                  {/* Progress bar background */}
                  <div className="absolute top-5 left-7 right-7 h-0.5 bg-pearl/40" />
                  {/* Progress bar active */}
                  <div
                    className="absolute top-5 left-7 h-0.5 bg-midnight transition-all duration-500"
                    style={{ width: `${Math.max(0, statusIdx / (STATUS_STEPS.length - 1)) * (100 - 10)}%` }}
                  />

                  {STATUS_STEPS.map((step, i) => {
                    const isComplete = i <= statusIdx;
                    const isCurrent = i === statusIdx;
                    return (
                      <div key={step.key} className="relative z-10 flex flex-col items-center" style={{ width: `${100 / STATUS_STEPS.length}%` }}>
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                            isCurrent
                              ? "bg-midnight text-white ring-4 ring-midnight/10"
                              : isComplete
                              ? "bg-midnight text-white"
                              : "bg-white border-2 border-pearl/60 text-pearl"
                          }`}
                        >
                          {isComplete && !isCurrent ? (
                            <i className="ri-check-line text-lg"></i>
                          ) : (
                            <i className={`${step.icon} text-lg`}></i>
                          )}
                        </div>
                        <span
                          className={`text-[10px] tracking-wide mt-2 text-center ${
                            isComplete ? "text-midnight font-medium" : "text-midnight/30"
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="px-6 md:px-8 pb-6">
              <h3 className="text-[10px] tracking-widest uppercase text-midnight/40 mb-4">Order Items</h3>
              <div className="space-y-3">
                {order.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 py-3 border-t border-pearl/20">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#f5f5f5] flex-shrink-0">
                      {item.image && (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-midnight font-medium truncate">{item.name}</p>
                      <p className="text-xs text-midnight/40 mt-0.5">
                        {item.size} · {item.color} · Qty {item.quantity}
                      </p>
                    </div>
                    <div className="text-sm text-midnight font-medium">
                      ${(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-4 pt-4 border-t border-pearl/30 space-y-1.5">
                <div className="flex justify-between text-xs text-midnight/40">
                  <span>Subtotal</span>
                  <span>${order.subtotal.toFixed(2)}</span>
                </div>
                {order.promoDiscount > 0 && (
                  <div className="flex justify-between text-xs text-emerald-600">
                    <span>Discount</span>
                    <span>-${order.promoDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs text-midnight/40">
                  <span>Shipping</span>
                  <span>{order.shippingCost === 0 ? "Free" : `$${order.shippingCost.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-xs text-midnight/40">
                  <span>Tax</span>
                  <span>${order.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium text-midnight pt-2 border-t border-pearl/20">
                  <span>Total</span>
                  <span>${order.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="mt-6 pt-4 border-t border-pearl/30">
                <h3 className="text-[10px] tracking-widest uppercase text-midnight/40 mb-2">Shipping Address</h3>
                <p className="text-sm text-midnight/70">
                  {order.firstName} {order.lastName}<br />
                  {order.address}<br />
                  {order.city}, {order.state} {order.zip}<br />
                  {order.country}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Help text */}
        <div className="text-center mt-10">
          <p className="text-xs text-midnight/30">
            Can't find your order? Check your confirmation email for your order number, or{" "}
            <Link to="/" className="text-midnight/50 underline hover:text-midnight transition-colors">
              contact us
            </Link>
            .
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
